/* eslint-disable @typescript-eslint/no-explicit-any */
"use client"

import { useEffect, useState } from "react"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Plus, Search, MoreHorizontal, Edit, Trash2, ChevronLeft, ChevronRight } from "lucide-react"
import { UserFormPopup } from "@/components/user-form-popup"
import { toast } from "sonner"
import bgImage from "@/assets/favicon.png"
import apiService from "@/services/apiService"
import useErrorStore from "@/store/confirmationStore"

interface User {
  id: string
  name: string
  email: string
  role: string
  department?: string
  status: "ACTIVE" | "INACTIVE" | "SUSPENDED"
  badgeNumber: string
  phone?: string
  joinDate: string
  password: string
  policeStation?: string[]
}

// Dummy user data
const initialUsersSample: User[] = [
  {
    id: "1",
    name: "Inspector Sharma",
    email: "sharma@police.gov.in",
    role: "INSPECTOR",
    department: "Crime Branch",
    status: "ACTIVE",
    badgeNumber: "INS001",
    phone: "+91 9876543210",
    joinDate: "2020-01-15",
    password: "1234567890"
  }
]

export default function UsersPage() {
  const [users, setUsers] = useState<User[]>(initialUsersSample)
  const [searchTerm, setSearchTerm] = useState("")
  const [showUserForm, setShowUserForm] = useState(false)
  const [editingUser, setEditingUser] = useState<User | null>(null)
  const [deleteUser, setDeleteUser] = useState<User | null>(null)
  const showError = useErrorStore((s) => s.showError)

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)

  const filteredUsers = users.filter(
    (user) =>
      user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.role.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.department?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.badgeNumber.toLowerCase().includes(searchTerm.toLowerCase()),
  )

  // Pagination calculations
  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const currentUsers = filteredUsers.slice(startIndex, endIndex)

  // Reset to first page when search changes
  const handleSearch = (value: string) => {
    setSearchTerm(value)
    setCurrentPage(1)
  }

  const handleCreateUser = () => {
    setEditingUser(null)
    setShowUserForm(true)
  }

  const handleEditUser = (user: User) => {
    setEditingUser(user)
    setShowUserForm(true)
  }

  const handleDeleteUser = (user: User) => {
    setDeleteUser(user)
  }

  const confirmDelete = async () => {
    if (deleteUser) {
      try {
        await apiService.delete(`users/${deleteUser.id}`);
        await fetchUsers();
        setDeleteUser(null);
        toast("Deleted successfully", {
          description: `${deleteUser.name} deleted successfully.`,
        });
      } catch (err: any) {
        showError(`Error deleting user: ${err.message}`);
      }
    }
  };


  const handleSaveUser = async (userData: Omit<User, "id">) => {
    if (editingUser) {
      const postParams:any = {...userData, id:editingUser.id,  joinDate: new Date(userData.joinDate).toISOString()}
      if(postParams && postParams.password == ""){
        delete postParams.password
      }
      // Update existing user
      const updatedUser = await updateUser(postParams);
      if(updatedUser){
        toast("Updated successfully", {
          description: "User updated successfully.",
        })
      }
      fetchUsers();
    } else {
      const user:any = await saveUser({...userData, joinDate: new Date(userData.joinDate).toISOString()});
      if(user){
        toast("Created successfully", {
          description: "User created successfully.",
        })
      }
      fetchUsers();
    }
    setShowUserForm(false)
    setEditingUser(null)
  }

  const fetchUsers = async () => {
    const jsonResponse:any  = await apiService.get('users');
    setUsers(jsonResponse)
  }

  async function saveUser(userData:Omit<User, "id">): Promise<string> {
    try {
      const data:any = await apiService.post('users', JSON.stringify(userData))
      return data.message || {}
    } catch (error) {
      console.error("Save user failed:", error)
      throw error
    }
  }

  async function updateUser(userData:User | any): Promise<string> {
    try {
      delete userData.policeStationDetails;
      delete userData.policeStationNames;
      delete userData.policeStations;
      const response:any = await apiService.put(`users/${userData.id}`, JSON.stringify(userData))
      if (!response) {
        throw new Error(`Server responded with status ${response.status}`)
      }
      toast.success("User updated successfully!")
      return response || {}
    } catch (error) {
      console.error("User updated failed:", error)
      throw error
    }
  }

  useEffect(() => {
    fetchUsers();
  },[])

  const getStatusBadge = (status: string) => {
    return (
      <Badge
        variant={status === "active" ? "default" : "secondary"}
        className={
          status === "active"
            ? "bg-green-100 text-green-800 hover:bg-green-100"
            : "bg-gray-100 text-gray-800 hover:bg-gray-100"
        }
      >
        {status}
      </Badge>
    )
  }

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  const handleItemsPerPageChange = (value: string) => {
    setItemsPerPage(Number.parseInt(value))
    setCurrentPage(1)
  }

  return (
    <div className="min-h-screen bg-gradient-to-r from-[#0d2846]/3 via-[#e2af58]/10 to-[#0d2846]/3 p-6">
      <div
        className="absolute inset-0 bg-no-repeat bg-center bg-contain opacity-3 pointer-events-none"
        style={{
          backgroundImage: `url(${bgImage})`,
        }}
      />
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-black">User Management</h1>
            <p className="text-gray-600 mt-1">Manage police personnel and their access</p>
          </div>
          <Button onClick={handleCreateUser} className="bg-gradient-to-r from-[#0d2846] to-[#0d2846]/90 hover:from-[#0d2846]/90 hover:to-[#0d2846] cursor-pointer text-white">
            <Plus className="h-4 w-4 mr-2" />
            Add User
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="border border-gray-200">
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-black">{users.length}</div>
              <p className="text-sm text-gray-600">Total Users</p>
            </CardContent>
          </Card>
          <Card className="border border-gray-200">
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-green-600">
                {users.filter((u) => u.status === "ACTIVE").length}
              </div>
              <p className="text-sm text-gray-600">Active Users</p>
            </CardContent>
          </Card>
          <Card className="border border-gray-200">
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-gray-600">
                {users.filter((u) => u.status === "INACTIVE").length}
              </div>
              <p className="text-sm text-gray-600">Inactive Users</p>
            </CardContent>
          </Card>
          <Card className="border border-gray-200">
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-blue-600">{new Set(users.map((u) => u.department)).size}</div>
              <p className="text-sm text-gray-600">Departments</p>
            </CardContent>
          </Card>
        </div>

        {/* Search and Filters */}
        <Card className="border border-gray-200">
          <CardHeader>
            <CardTitle className="text-lg text-black">Users List</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between mb-6">
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  placeholder="Search users..."
                  value={searchTerm}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="pl-10 border-gray-300"
                />
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Show:</span>
                <Select value={itemsPerPage.toString()} onValueChange={handleItemsPerPageChange}>
                  <SelectTrigger className="w-20">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="5">5</SelectItem>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="20">20</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                  </SelectContent>
                </Select>
                <span className="text-sm text-gray-600">per page</span>
              </div>
            </div>

            {/* Users Table */}
            <div className="border border-gray-200 rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead className="text-black font-semibold">Name</TableHead>
                    <TableHead className="text-black font-semibold">Badge No.</TableHead>
                    <TableHead className="text-black font-semibold">Role</TableHead>
                    <TableHead className="text-black font-semibold">Department</TableHead>
                    <TableHead className="text-black font-semibold">Email</TableHead>
                    <TableHead className="text-black font-semibold">Phone</TableHead>
                    <TableHead className="text-black font-semibold">Status</TableHead>
                    <TableHead className="text-black font-semibold">Join Date</TableHead>
                    <TableHead className="text-black font-semibold w-[50px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {currentUsers.filter((user) => user.name != 'Admin').map((user) => (
                    <TableRow key={user.id} className="hover:bg-gray-50">
                      <TableCell className="font-medium text-black">{user.name}</TableCell>
                      <TableCell className="text-gray-600">{user.badgeNumber}</TableCell>
                      <TableCell className="text-gray-600">{user.role}</TableCell>
                      <TableCell className="text-gray-600">{user.department}</TableCell>
                      <TableCell className="text-gray-600">{user.email}</TableCell>
                      <TableCell className="text-gray-600">{user.phone}</TableCell>
                      <TableCell>{getStatusBadge(user.status)}</TableCell>
                      <TableCell className="text-gray-600">{new Date(user.joinDate).toLocaleDateString("en-IN")}</TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" className="h-8 w-8 p-0 hover:bg-gray-100">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="bg-white border border-gray-200">
                            <DropdownMenuItem
                              onClick={() => handleEditUser(user)}
                              className="hover:bg-gray-50 cursor-pointer"
                            >
                              <Edit className="h-4 w-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => handleDeleteUser(user)}
                              className="hover:bg-red-50 text-red-600 cursor-pointer"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {currentUsers.length === 0 && (
                <div className="text-center py-8 text-gray-500">No users found matching your search criteria.</div>
              )}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-4">
                <div className="text-sm text-gray-600">
                  Showing {startIndex + 1} to {Math.min(endIndex, filteredUsers.length)} of {filteredUsers.length} users
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="border-gray-300"
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Previous
                  </Button>

                  <div className="flex items-center gap-1">
                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                      .filter((page) => {
                        // Show first page, last page, current page, and pages around current
                        return page === 1 || page === totalPages || Math.abs(page - currentPage) <= 1
                      })
                      .map((page, index, array) => (
                        <div key={page} className="flex items-center">
                          {index > 0 && array[index - 1] !== page - 1 && (
                            <span className="px-2 text-gray-400">...</span>
                          )}
                          <Button
                            variant={currentPage === page ? "default" : "outline"}
                            size="sm"
                            onClick={() => handlePageChange(page)}
                            className={currentPage === page ? "bg-black text-white" : "border-gray-300"}
                          >
                            {page}
                          </Button>
                        </div>
                      ))}
                  </div>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="border-gray-300"
                  >
                    Next
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* User Form Popup */}
      <UserFormPopup open={showUserForm} onOpenChange={setShowUserForm} user={editingUser} onSave={handleSaveUser} />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteUser} onOpenChange={() => setDeleteUser(null)}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-black">Delete User</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-600">
              Are you sure you want to delete {deleteUser?.name}? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-gray-300">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 text-white hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
